import React, { useEffect, useState } from 'react';
import DefaultModal from '../../../components/default/DefaultModal';
import SelectTwo, { SelectTwoMapperOptions } from '../../../components/SelectTwo';
import useProductService from '../../../app/services/hooks/useProductService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';

export interface RecipeLine {
  id: number;
  product_id: number;
  ingredient_product_id: number;
  ingredient_product_name: string;
  ingredient_unit_name: string | null;
  quantity: string | number;
}

interface Props {
  show: boolean;
  productId: number;
  onClose: () => void;
}

const ProductRecipeModal: React.FC<Props> = ({ show, productId, onClose }) => {
  const {
    getProductRecipe,
    storeProductRecipeLine,
    deleteProductRecipeLine,
    getProducts,
    fetchingGetProductRecipe,
    fetchingStoreProductRecipeLine,
  } = useProductService();

  const confirmAlert = useReactConfirmAlert();

  const [lines, setLines] = useState<RecipeLine[]>([]);
  const [productOptions, setProductOptions] = useState<{ id: number; name: string; unit?: string | null }[]>([]);
  const [ingredientProductId, setIngredientProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<string>('1');
  const [addError, setAddError] = useState<string | null>(null);

  const selectedProduct = productOptions.find((p) => p.id === ingredientProductId);
  const selectedUnitName = selectedProduct?.unit ?? null;

  const loadRecipe = () => {
    if (!productId) return;
    getProductRecipe(productId, {
      onSuccess: (res: ServiceResponse) => {
        setLines(res.data?.recipe ?? []);
      },
      onError: (res: ServiceResponse) => {
        toast.error(res.message || 'Error al cargar la receta');
      },
    });
  };

  const loadProductOptions = () => {
    getProducts(
      { page: 1, per_page: 500, sort: 'name', order: 'asc' },
      {
        onSuccess: (res: ServiceResponse) => {
          const list = (res.data?.products ?? []).map((p: any) => ({
            id: p.id,
            name: p.name || '',
            unit: p.unit ?? null,
          }));
          setProductOptions(list);
        },
      }
    );
  };

  useEffect(() => {
    if (show && productId) {
      loadRecipe();
      loadProductOptions();
      setIngredientProductId(null);
      setQuantity('1');
      setAddError(null);
    }
  }, [show, productId]);

  const handleAdd = () => {
    setAddError(null);
    const n = parseInt(quantity, 10);
    if (!ingredientProductId || isNaN(n) || n <= 0) {
      setAddError('Seleccione un producto e ingrese una cantidad entera mayor o igual a 1.');
      return;
    }
    const qty = n;
    storeProductRecipeLine(
      productId,
      { ingredient_product_id: ingredientProductId, quantity: qty },
      {
        onSuccess: () => {
          toast.success('Ingrediente agregado.');
          setIngredientProductId(null);
          setQuantity('1');
          loadRecipe();
        },
        onError: (res: ServiceResponse) => {
          const msg = res.message || 'Error al agregar ingrediente.';
          toast.error(msg);
          confirmAlert.errorAlert({
            title: 'Error',
            message: msg,
          });
          if (res.data) {
            setAddError(typeof res.data === 'string' ? res.data : msg);
          }
        },
        onFieldError: (res: ServiceResponse) => {
          // Manejo específico de errores de validación de campos (ej: ingrediente duplicado)
          let msg: string = res.message || 'Error de validación.';
          const data: any = res.data;
          if (data && data.ingredient_product_id && Array.isArray(data.ingredient_product_id)) {
            msg = data.ingredient_product_id[0] || msg;
          }
          setAddError(msg);
          confirmAlert.errorAlert({
            title: 'Error',
            message: msg,
          });
        },
      }
    );
  };

  const handleDelete = (lineId: number) => {
    deleteProductRecipeLine(lineId, {
      onSuccess: () => {
        toast.success('Ingrediente quitado de la receta.');
        loadRecipe();
      },
      onError: (res: ServiceResponse) => {
        toast.error(res.message || 'Error al eliminar');
      },
    });
  };

  const selectOptions = SelectTwoMapperOptions(productOptions, 'id', ['name']);

  return (
    <DefaultModal
      show={show}
      handleClose={onClose}
      title="Receta del producto"
      showFooter={false}
      backdrop={true}
      size="lg"
    >
      <p className="text-muted small mb-3">
        Productos y cantidades necesarias para elaborar este producto.
      </p>

      <div className="table-responsive mb-4">
        <table className="table table-sm table-striped">
          <thead className="table-light">
            <tr>
              <th>Producto</th>
              <th style={{ width: '120px', textAlign: 'right' }}>Cantidad</th>
              <th>Unidad</th>
              <th style={{ width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {fetchingGetProductRecipe ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">Cargando...</td>
              </tr>
            ) : lines.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">Sin ingredientes. Agregue productos abajo.</td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.ingredient_product_name ?? '-'}</td>
                  <td style={{ textAlign: 'right' }}>{Number(line.quantity).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                  <td>{line.ingredient_unit_name ?? '-'}</td>
                  <td>
                    <ButtonTableAction
                      title="Quitar"
                      classIcon="mdi mdi-delete"
                      colorIcon="text-danger"
                      callbackFunction={() => handleDelete(line.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <hr />
      <h6 className="mb-2">Agregar ingrediente</h6>
      <div className="row g-2 align-items-end">
        <div className="col-md-5">
          <label className="form-label small">Producto</label>
          <SelectTwo
            name="ingredient_product"
            inputValue={ingredientProductId}
            options={selectOptions}
            onChange={(opt: any) => { setIngredientProductId(opt?.value ?? null); setAddError(null); }}
            placeholder="Seleccione producto"
            isClearable
          />
        </div>
        <div className="col-md-2">
          <label className="form-label small">Unidad</label>
          <div className="form-control bg-light">
            {selectedUnitName ?? '—'}
          </div>
        </div>
        <div className="col-md-2">
          <label className="form-label small">Cantidad</label>
          <input
            type="number"
            className="form-control"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            min="1"
            step="1"
          />
        </div>
        <div className="col-md-3">
          <button
            type="button"
            className="btn btn-success w-100"
            onClick={handleAdd}
            disabled={!!fetchingStoreProductRecipeLine}
          >
            {fetchingStoreProductRecipeLine ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
      </div>
      {addError ? <div className="text-danger small mt-1">{addError}</div> : <></>}

      <div className="d-flex justify-content-end mt-3">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </DefaultModal>
  );
};

export default ProductRecipeModal;
